/**
 * A moldura de seleção da marca — o aparato que o nome do sistema desenha em
 * volta da letra sob a lente, e que o botão desenha em volta de si mesmo.
 *
 * Fica aqui, fora dos dois componentes, para ser **o mesmo desenho** nos dois:
 * o quadro fino com as cantoneiras, as faíscas quadradas (cheias e vazias, de
 * vários tamanhos e tons, piscando em posições sorteadas por semente), as
 * duas cabeças correndo pela borda em pontas opostas com rastro, e o rótulo
 * de medida em mono acima do canto. Adaptado do "Tech Text" do reactbits.
 */

export type Rgb = readonly [number, number, number];

export interface FrameRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface FrameStyle {
  /** Cor do aparato — quadro, cantoneiras, faíscas, cabeças e rótulo. */
  accent: Rgb;
  /** Quantas faíscas em volta. Zero desliga. */
  specks: number;
  /** Escala dos adereços: a referência foi desenhada para letras de 150px. */
  scale: number;
  /** Velocidade das cabeças que correm pela borda. */
  speed: number;
  /** Fonte do rótulo de medida. */
  labelFont: string;
}

/** Rótulo de medida, na fonte técnica do sistema. */
export const LABEL_FONT = "10px 'Geist Mono Variable', ui-monospace, monospace";

export const approach = (current: number, target: number, dt: number, seconds: number): number =>
  current + (target - current) * (1 - Math.exp(-dt / seconds));

/** Aceita `rgb(a)` do `getComputedStyle` e hex de 3 ou 6 dígitos. */
export function toRgb(value: string): Rgb {
  const rgb = value.match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];

  let hex = value.trim().replace('#', '');
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
  const n = parseInt(hex.slice(0, 6), 16);
  return Number.isNaN(n) ? [255, 255, 255] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const rgba = (color: Rgb, alpha: number): string =>
  `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;

/** Ruído determinístico: a mesma semente dá sempre a mesma faísca. */
export function noise(...values: number[]): number {
  let h = 2166136261;
  for (const value of values) {
    h = Math.imul(h ^ (value | 0), 16777619);
    h ^= h >>> 13;
    h = Math.imul(h, 0x5bd1e995);
    h ^= h >>> 15;
  }
  return (h >>> 0) / 4294967296;
}

/** Meio pixel físico: o traço de 1px cai inteiro num pixel, sem borrar. */
export const crisp = (value: number, dpr: number): number => (Math.round(value * dpr) + 0.5) / dpr;

/**
 * Um ponto a `distance` do canto superior esquerdo, andando pela borda do
 * quadro no sentido horário, com a normal para fora.
 */
function perimeterPoint(
  frame: FrameRect,
  distance: number,
): [x: number, y: number, nx: number, ny: number] {
  const w = frame.x2 - frame.x1;
  const h = frame.y2 - frame.y1;
  const total = 2 * (w + h);
  let d = ((distance % total) + total) % total;
  if (d < w) return [frame.x1 + d, frame.y1, 0, -1];
  d -= w;
  if (d < h) return [frame.x2, frame.y1 + d, 1, 0];
  d -= h;
  if (d < w) return [frame.x2 - d, frame.y2, 0, 1];
  d -= w;
  return [frame.x1, frame.y2 - d, -1, 0];
}

/**
 * As faíscas e as cabeças.
 *
 * Cada faísca tem um período e uma fase próprios (sorteados pela semente e
 * pelo índice), vive 70% do ciclo e pisca duas vezes nele. O tamanho sai de
 * cinco degraus; as menores são cheias, as grandes são vazias com um ponto
 * no meio, e o tom varia — é isso que dá a impressão de profundidade. Tudo
 * ancorado numa grade, como na referência.
 *
 * As duas cabeças partem de pontas opostas (meia volta de diferença) e
 * arrastam três quadradinhos cada, cada vez mais fracos.
 */
function drawSpecks(
  ctx: CanvasRenderingContext2D,
  frame: FrameRect,
  seed: number,
  pulse: number,
  alpha: number,
  style: FrameStyle,
): void {
  const w = frame.x2 - frame.x1;
  const h = frame.y2 - frame.y1;
  if (w < 2 || h < 2) return;
  const perimeter = 2 * (w + h);
  const grid = Math.max(1, Math.round(3 * style.scale * 2));

  ctx.lineWidth = 1;
  for (let k = 0; k < style.specks; k++) {
    const period = 0.5 + noise(seed, k, 11) * 1.2;
    const t = pulse / period + noise(seed, k, 17);
    const cycle = Math.floor(t);
    const life = t - cycle;
    if (life > 0.7) continue;

    const [px, py, nx, ny] = perimeterPoint(frame, noise(seed, k, cycle) * perimeter);
    const pick = noise(seed, k, cycle, 2);
    const base = pick < 0.46 ? 2 : pick < 0.7 ? 3 : pick < 0.84 ? 5 : pick < 0.94 ? 8 : 11;
    const size = Math.max(1, Math.round(base * style.scale * 2));
    const large = base >= 8;
    const out = (large ? 9 : 4) * style.scale * 2 + Math.floor(noise(seed, k, cycle, 1) * 5) * grid;
    const x = frame.x1 + Math.round((px + nx * out - frame.x1) / grid) * grid;
    const y = frame.y1 + Math.round((py + ny * out - frame.y1) / grid) * grid;
    const tone = noise(seed, k, cycle, 3);
    const blink = life < 0.06 || (life > 0.32 && life < 0.36) ? 0.35 : 1;
    const a = alpha * (large ? 0.3 + 0.4 * tone : 0.3 + 0.6 * tone) * blink;
    const left = Math.round(x - size / 2);
    const top = Math.round(y - size / 2);

    if (tone < 0.26 || (large && tone < 0.78)) {
      ctx.strokeStyle = rgba(style.accent, a);
      ctx.strokeRect(left + 0.5, top + 0.5, size, size);
      if (large && tone > 0.5) {
        ctx.fillStyle = rgba(style.accent, a);
        ctx.fillRect(Math.round(x) - 1, Math.round(y) - 1, 2, 2);
      }
    } else {
      ctx.fillStyle = rgba(style.accent, a);
      ctx.fillRect(left, top, size, size);
    }
  }

  for (let j = 0; j < 2; j++) {
    const head = (pulse * 0.42 * style.speed + j * 0.5) * perimeter;
    for (let i = 0; i < 4; i++) {
      const [x, y] = perimeterPoint(frame, head - i * 6);
      const size = i === 0 ? 3 : 2;
      ctx.fillStyle = rgba(style.accent, alpha * [0.95, 0.55, 0.32, 0.16][i]!);
      ctx.fillRect(Math.round(x - size / 2), Math.round(y - size / 2), size, size);
    }
  }
}

/**
 * O aparato inteiro: quadro a meia opacidade, cantoneiras de 5px centradas
 * nos cantos, faíscas e cabeças, e o rótulo acima do canto esquerdo.
 * `alpha` é a presença do conjunto (sobe ao entrar, desce ao sair).
 */
export function drawTechFrame(
  ctx: CanvasRenderingContext2D,
  frame: FrameRect,
  seed: number,
  pulse: number,
  alpha: number,
  style: FrameStyle,
  dpr: number,
  label?: string,
): void {
  if (alpha < 0.01) return;
  const x1 = crisp(frame.x1, dpr);
  const y1 = crisp(frame.y1, dpr);
  const x2 = crisp(frame.x2, dpr);
  const y2 = crisp(frame.y2, dpr);

  ctx.beginPath();
  ctx.rect(x1, y1, x2 - x1, y2 - y1);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(style.accent, 0.5 * alpha);
  ctx.stroke();

  ctx.beginPath();
  for (const [cx, cy] of [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ] as const) {
    ctx.rect(Math.round(cx) - 2, Math.round(cy) - 2, 5, 5);
  }
  ctx.fillStyle = rgba(style.accent, 0.95 * alpha);
  ctx.fill();

  if (style.specks > 0) drawSpecks(ctx, frame, seed, pulse, alpha, style);

  if (!label) return;
  ctx.font = style.labelFont;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = rgba(style.accent, 0.62 * alpha);
  ctx.fillText(label, Math.round(frame.x1), Math.round(frame.y1) - 6);
}
